<script setup lang="ts">
import { ref, useId } from 'vue';
withDefaults(
  defineProps<{
    title: string;
    count: number;
    loading?: boolean;
    error?: string;
    retryable?: boolean;
  }>(),
  { loading: false, error: '' },
);
const emit = defineEmits<{ retry: [] }>();
const expanded = ref(false);
const contentId = useId();
</script>

<template>
  <section
    class="task-group"
    :class="loading || error ? 'task-unknown' : count > 0 ? 'task-pending' : 'task-clear'"
  >
    <h3>
      <button
        type="button"
        class="task-heading"
        :class="loading || error ? 'task-unknown' : count > 0 ? 'task-pending' : 'task-clear'"
        :aria-expanded="expanded"
        :aria-controls="contentId"
        @click="expanded = !expanded"
      >
        <span class="task-title"
          >{{ title }}{{ ' '
          }}<small v-if="loading || error">{{
            loading ? 'Chargement…' : 'Indisponible'
          }}</small></span
        >
        <strong v-if="!loading && !error" class="task-count">({{ count }})</strong>
        <span class="task-toggle" aria-hidden="true">{{ expanded ? '−' : '+' }}</span>
      </button>
    </h3>
    <div v-if="expanded" :id="contentId" class="task-content">
      <p v-if="loading" role="status">Chargement…</p>
      <p v-else-if="error" role="alert">{{ error }}</p>
      <template v-else
        ><p v-if="count === 0">Rien à traiter.</p>
        <slot
      /></template>
      <button v-if="error && retryable" type="button" @click="emit('retry')">
        Réessayer le chargement
      </button>
    </div>
  </section>
</template>

<style scoped>
.task-group {
  margin: 0.8rem 0;
  border: 1px solid #dce5df;
  border-left: 5px solid var(--task-accent);
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}
.task-pending {
  --task-accent: #b91c1c;
  --task-text: #991b1b;
}
.task-clear {
  --task-accent: #166534;
  --task-text: #14532d;
}
.task-unknown {
  --task-accent: #475569;
  --task-text: #475569;
}
h3 {
  margin: 0;
  font-size: 1rem;
}
.task-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
  min-height: 56px;
  padding: 0.85rem;
  border: 0;
  border-radius: 0;
  background: #fff;
  color: var(--task-text);
  text-align: left;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}
.task-title {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}
.task-count {
  flex-shrink: 0;
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  background: var(--task-accent);
  color: #fff;
}
.task-toggle {
  font-size: 1.35rem;
}
.task-heading:focus-visible {
  outline: 3px solid #1d4ed8;
  outline-offset: -3px;
}
.task-heading small {
  display: block;
  font-size: 0.85rem;
  font-weight: 400;
}
.task-content {
  padding: 0 0.85rem 0.85rem;
  overflow-wrap: anywhere;
}
</style>
